import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { SimilarStructure } from '../../../../entry/structure-data.models';
import { ConfigService } from '../../../../config.service';
import { CommonService } from '../../../../common.service';
import { GoogleAnalyticsService } from '../../../../google-analytics.service';
import { HttpResponse } from '@angular/common/http';
import { CheckboxService } from '../checkbox.service';
import { Subscription } from 'rxjs';
import { FoldseekService } from '../../../../foldseek-table/foldseek.service';
import { Sort } from '@angular/material/sort';

@Component({
  selector: 'mb-bfvd-tab',
  templateUrl: './mb-bfvd-tab.component.html',
  styleUrls: ['./mb-bfvd-tab.component.css', '../mb-foldseek-table.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class MbBfvdTabComponent implements OnInit, OnDestroy {
  @Input() accession: string;
  @Input() afdb_accession: string;
  @Output() toggleClickedEvent: EventEmitter<Object> = new EventEmitter();

  displayedColumnsBFVDStructure: string[] = ['expand', 'alighin3D', 'afdbAccessions', 'speciesNames', 'sequenceLength', 'evalue', 'sequenceIdentity', 'averagePlddt'];
  bfvdStructureDataSource = new MatTableDataSource<SimilarStructure>();
  isBFVDLoading = false;
  bfvdZeroResultsMsg = "";
  errorBFVD = "";
  isDatabaseErrorBFVD = false;
  startBFVD = "0";
  recordsBFVD = "10";
  currentBFVDPage = 0;
  sort_direction_BFVD = 'asc';
  sort_column_BFVD = 'evalue';
  filteritemBFVD = "";
  species_name_filter_BFVD = "";
  expandedElementsBFVD = [];
  dataLoaded = false;

  checkboxState: { [key: string]: boolean } = {};
  disabledState: { [key: string]: boolean } = {};
  private subscription: Subscription;
  apiData = {};

  constructor(
    private commonService: CommonService,
    private configService: ConfigService,
    public gaService: GoogleAnalyticsService,
    private changeDetectorRef: ChangeDetectorRef,
    private checkboxService: CheckboxService,
    public foldseekService: FoldseekService
  ) {}

  ngOnInit(): void {
    this.subscription = this.checkboxService.checkboxState$.subscribe(state => {
      this.checkboxState = state;
      this.changeDetectorRef.detectChanges();
    });
    this.checkboxService.disabledState$.subscribe(state => {
      this.disabledState = state;
    });
    this.dataLoaded = true;
    this.loadData(this.recordsBFVD, this.startBFVD, this.sort_direction_BFVD, this.sort_column_BFVD, this.species_name_filter_BFVD);
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  clickToggleSuperpose(event, accession) {
    this.toggleClickedEvent.emit({ event, accession, apidata: this.apiData[accession] });
  }

  toggleRowBFVD(element) {
    const ele = this.expandedElementsBFVD.find(e => e.afdbAccessions === element.afdbAccessions);
    if (ele) {
      this.expandedElementsBFVD = this.expandedElementsBFVD.filter(el => el.afdbAccessions !== element.afdbAccessions);
    } else {
      this.expandedElementsBFVD.push(element);
    }
  }

  isExpandedBFVD(element): boolean {
    return !!this.expandedElementsBFVD.find(e => e.afdbAccessions === element.afdbAccessions);
  }

  isExpansionDetailRowBFVD = (i: number, row) => this.isExpandedBFVD(row);

  checkAndLoadData() {
    if (this.dataLoaded) return;
    this.dataLoaded = true;
    this.loadData(this.recordsBFVD, this.startBFVD, this.sort_direction_BFVD, this.sort_column_BFVD, this.species_name_filter_BFVD);
  }

  loadData(records = "", start = "", sort_direction = 'asc', sort_column = 'evalue', species_name_filter = "") {
    const auth = this.configService.getConfig().apiKey ? `&key=${this.configService.getConfig().apiKey}` : "";
    const configUrl = this.configService.getConfig().searchApiUrl || "https://test.alphafold.ebi.ac.uk/api/search";
    let apiUrl = `${configUrl}?q=${this.accession}&type=structure&foldseek_database=bfvd`;
    if (records) apiUrl += `&rows=${records}`;
    if (start) apiUrl += `&start=${start}`;
    if (sort_column && sort_direction) {
      if (sort_column === "evalue") apiUrl += `&sort=${sort_column}+${sort_direction}`;
      if (sort_column === "sequenceIdentity") apiUrl += `&sort=pident+${sort_direction}`;
    }
    if (species_name_filter) apiUrl += `&fq=organismScientificName%3A*${species_name_filter.trim()}*`;

    this.isBFVDLoading = true;
    this.commonService.getApiDatawithStatus(apiUrl + auth).subscribe(async (response: HttpResponse<any>) => {
      const code = await Number(response.status);
      if (code === 202) {
        if (response.body.message && (response.body.message.startsWith("Foldseek search job is currently running") || response.body.message.startsWith("Initiating new Foldseek search"))) {
          setTimeout(() => {
            if (this.isBFVDLoading) this.loadData(records, start, sort_direction, sort_column, species_name_filter);
          }, 15000);
        }
      }
      if (code === 200) {
        if (response.body.numFound !== 0) {
          const data = response.body.docs;
          this.foldseekService.updateTotalBFVD(response.body.numFound);
          this.errorBFVD = "";
          this.bfvdZeroResultsMsg = "";
          const similarStructureData = this.transformBFVDStructureData(data);
          this.bfvdStructureDataSource = new MatTableDataSource<SimilarStructure>(similarStructureData);
        } else {
          this.bfvdZeroResultsMsg = "No matching structures were found in BFVD.";
        }
        this.isBFVDLoading = false;
        this.changeDetectorRef.detectChanges();
      }
    },
    (error) => {
      const code = error.status;
      this.isBFVDLoading = false;
      if (code === 404) {
        if (error.error.message === "The search was completed successfully, but no matching results were found") {
          this.bfvdZeroResultsMsg = "No matching structures were found in BFVD.";
        } else if (species_name_filter !== "") {
          this.errorBFVD = "No species match your search";
          this.bfvdZeroResultsMsg = "";
        }
      }
      if (code === 422 || code === 302 || code === 500 || code === 0 || code === 429) {
        this.isDatabaseErrorBFVD = true;
      }
      this.changeDetectorRef.detectChanges();
    });
  }

  handlePageEvent(e) {
    const start = (e.pageIndex * e.pageSize).toString();
    this.startBFVD = start;
    this.recordsBFVD = e.pageSize.toString();
    this.currentBFVDPage = e.pageIndex;
    this.loadData(this.recordsBFVD, start, this.sort_direction_BFVD, this.sort_column_BFVD, this.filteritemBFVD);
    this.gaService.eventEmitter('foldseek_table_action', 'FS_similar_structures', 'click', 'foldseek_paginate', 'Clicks on the paginations in the Foldseek results table');
  }

  sortTable(sortState: Sort) {
    const directions = { "ASC": "DESC", "DESC": "ASC" };
    this.sort_direction_BFVD = directions[sortState.direction.toUpperCase()];
    this.sort_column_BFVD = sortState.active;
    this.loadData(this.recordsBFVD, this.startBFVD, this.sort_direction_BFVD, this.sort_column_BFVD, this.filteritemBFVD);
  }

  filterTable(filterby) {
    this.currentBFVDPage = 0;
    this.filteritemBFVD = filterby;
    this.startBFVD = "0";
    this.species_name_filter_BFVD = filterby;
    this.loadData(this.recordsBFVD, "0", this.sort_direction_BFVD, this.sort_column_BFVD, filterby);
    this.gaService.eventEmitter('foldseek_table_action', 'FS_similar_structures', 'click', 'foldseek_taxfilter', 'Clicks on the "Taxonomy filter" input box on the Foldseek results table');
  }

  clearFilter() {
    this.currentBFVDPage = 0;
    this.filteritemBFVD = "";
    this.species_name_filter_BFVD = "";
    this.loadData(this.recordsBFVD, "0", this.sort_direction_BFVD, this.sort_column_BFVD, "");
    this.changeDetectorRef.detectChanges();
  }

  getTitle(accession, reviewedStatus, referenceLabel) {
    if (!accession) return "Unavailable";
    return `${accession}*${referenceLabel}*${reviewedStatus}`;
  }

  getLink(database, target) {
    if (database === "pdb") {
      const [entry, , protein] = target.split("_");
      return protein ?
        `${this.configService.getConfig().pdbeBaseApiUrl}/entry/pdb/${entry}/protein/${protein}` :
        `${this.configService.getConfig().pdbeBaseApiUrl}/entry/pdb/${entry}`;
    }
    return `/entry/${target}`;
  }

  transformBFVDStructureData(data) {
    const similarClusterData = [];
    if (data) {
      const databaseInfo = {};
      for (let i = 0; i < data.length; i++) {
        similarClusterData.push({
          "refLinkId": this.getLink(data[i].database, data[i].target),
          "afdbAccessions": this.getTitle(data[i].target, data[i].isReviewed, data[i].isReferenceProteome),
          "targetToShow": data[i].modelEntityId,
          "uniprotDescriptions": data[i].uniprotDescription,
          "speciesNames": data[i].organismScientificName ? data[i].organismScientificName : "",
          "sequenceIdentity": data[i].pident ? data[i].pident : "N/A",
          "sequenceLength": data[i].uniprotEnd ? `1-${data[i].uniprotEnd}` : "N/A",
          "evalue": (data[i].evalue != null && data[i].evalue !== "") ? Number(data[i].evalue).toExponential(2) : "N/A",
          "averagePlddt": data[i].globalMetricValue ? data[i].globalMetricValue : "N/A",
          "alighin3D": false,
          "expanded": false,
          "hsps": {
            "current_entry": this.afdb_accession,
            "accession": data[i].target,
            "query_sequence": data[i].qaln,
            "match_sequence": data[i].qaln,
            "match_accession": data[i].taln,
            "qstart": data[i].qstart,
            "qend": data[i].qend,
            "tstart": data[i].tstart,
            "tend": data[i].tend,
            "sequence_stats": [],
            "targetToShow": data[i].entryId,
          }
        });

        data[i].database = "afdb50";
        const foldseekWithDesc = {
          ...data[i],
          targetToShow: data[i].entryId,
          desc: data[i].uniprotDescription,
          refLinkId: this.getLink(data[i].database, data[i].target),
        };
        foldseekWithDesc.tstructure = {
          "url": `https://alphafold.ebi.ac.uk/files/${data[i].modelEntityId}-model_v2.bcif`,
          "format": 'cif',
          "binary": true,
        };
        databaseInfo[data[i].target] = foldseekWithDesc;
      }
      this.apiData = { ...this.apiData, ...databaseInfo };
    }
    return similarClusterData;
  }
}
